import { useEffect } from 'react'
import useLocalStorage, { setLocalStorage } from '@based/use-local-storage'

/**
 * Defensive wrapper around `useLocalStorage`.
 *
 * The underlying hook can hand back `null` even when a `defaultValue` is
 * provided (e.g. another caller wrote `null` for the same key, or the stored
 * JSON happens to be the literal `"null"`). It also has no concept of "the
 * stored value is no longer valid" — schemas drift (a removed locale, a
 * decommissioned voting region) and stale localStorage outlives a deploy.
 *
 * This wrapper guarantees:
 *   - `null` / `undefined` are treated as missing → consumer sees `defaultValue`
 *   - if `validate` is provided and rejects the stored value, the consumer
 *     sees `defaultValue` and the bogus value is cleared from storage
 */
export const useSafeLocalStorage = <T>(
  key: string,
  defaultValue: T,
  validate?: (value: T) => boolean,
): [T, (value: T) => void] => {
  const [stored, setStored] = useLocalStorage<T>(key, defaultValue)

  const present = stored !== null && stored !== undefined
  const isValid = present && (!validate || validate(stored as T))
  const value = isValid ? (stored as T) : defaultValue

  useEffect(() => {
    if (present && !isValid) {
      setLocalStorage(key, null)
    }
  }, [key, present, isValid])

  return [value, setStored]
}

export default useSafeLocalStorage
